import jsPDF from "jspdf";
import { AssessmentInputs } from "../components/TheAssessorModule";
import { ensureRaleway } from "../src/pdf/fonts/raleway";
/* ======================================================
   Assessor PDF Generator (single file, all artifacts)
   - A-Typical geometry: A4 mm, header band, footer
   - Block-based rendering: deterministic page breaks
   - Artifact-aware parsing:
     - Question Paper
     - Answer Key
     - Marking Rubric
     - Assessment Blueprint
   - Markdown normalization (removes ##, **, etc.)
   ====================================================== */

const BRAND_ORANGE = "#F75C03";
const BRAND_GREY = "#4D4D4D";
const TEXT_COLOR = "#4D4D4D";
const LIGHT_GREY = "#F5F5F5";
const FOOTER_GREY = "#8C8C8C";
const BORDER_GREY = "#DDDDDD";

type FontFamily = "Raleway" | "helvetica";

let doc: jsPDF;
let pageHeight: number;
let pageWidth: number;
let yCursor: number;
let FONT_FAMILY: FontFamily = "helvetica";

// A-Typical geometry (mm, a4)
const MARGIN = 25;
const HEADER_TOP = 10;
const HEADER_H = 28;
const BODY_START_Y = HEADER_TOP + HEADER_H + 8;
const FOOTER_RESERVED = 18;

// Typography
const H1_SIZE = 18;
const H2_SIZE = 13;
const BODY_SIZE = 11;
const SMALL_SIZE = 10;
const FOOTER_SIZE = 8;

const LINE_HEIGHT = 6.0; // stable line height in mm-ish
const PARA_GAP = 2.6;
const SECTION_GAP = 4.5;

/* ======================================================
   Normalization (removes markdown artifacts)
   ====================================================== */

function normalizeAssessorText(raw: string): string {
  if (!raw) return "";

  let t = raw.replace(/\r\n/g, "\n");

  // Remove markdown headers (##, ###, etc.)
  t = t.replace(/^#{1,6}\s*/gm, "");

  // Remove bold/italics markers
  t = t.replace(/\*\*(.*?)\*\*/g, "$1");
  t = t.replace(/\*(.*?)\*/g, "$1");

  // Remove code fences (defensive)
  t = t.replace(/^```(?:\w+)?\s*$/gm, "");
  t = t.replace(/^```\s*$/gm, "");

  // Remove underline-style headers
  t = t.replace(/^\s*[-=]{3,}\s*$/gm, "");

  // Collapse excessive blank lines
  t = t.replace(/\n{3,}/g, "\n\n");

  return t.trim();
}

/* ======================================================
   Color helpers
   ====================================================== */

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = String(hex || "").replace("#", "").trim();
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function setTextHex(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  doc.setTextColor(r, g, b);
}

function setDrawHex(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  doc.setDrawColor(r, g, b);
}

function setFillHex(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  doc.setFillColor(r, g, b);
}

/* ======================================================
   Page geometry
   ====================================================== */

function bottomLimit() {
  return pageHeight - MARGIN - FOOTER_RESERVED;
}

function addPage() {
  doc.addPage();
  yCursor = BODY_START_Y;
}

function ensureSpace(spaceNeeded: number) {
  if (yCursor + spaceNeeded > bottomLimit()) addPage();
}

function safeAddImage(dataUrl: string | undefined, x: number, y: number, w: number, h: number) {
  try {
    const s = String(dataUrl || "");
    if (!s.startsWith("data:image/")) return;

    const isPng = s.startsWith("data:image/png");
    const isJpg = s.startsWith("data:image/jpeg") || s.startsWith("data:image/jpg");
    const fmt = isPng ? "PNG" : isJpg ? "JPEG" : null;
    if (!fmt) return;

    doc.addImage(s, fmt as any, x, y, w, h, undefined, "FAST");
  } catch {
    // best-effort
  }
}
/**
 * Block renderer with internal page breaks.
 * Never wrap this with external page-break checks.
 */
function renderBlock(args: {
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  lineHeight: number;
  fontSize?: number;
  fontStyle?: "normal" | "bold";
}) {
  const {
    text,
    x,
    y,
    maxWidth,
    lineHeight,
    fontSize = BODY_SIZE,
    fontStyle = "normal",
  } = args;

  doc.setFont(FONT_FAMILY, fontStyle);
  doc.setFontSize(fontSize);

  const safeText = String(text || "").replace(/\r\n/g, "\n").trimEnd();
  const lines = doc.splitTextToSize(safeText, maxWidth);

  let yy = y;

  for (const ln of lines) {
    if (yy + lineHeight > bottomLimit()) {
      addPage();
      yy = yCursor;
      doc.setFont(FONT_FAMILY, fontStyle);
      doc.setFontSize(fontSize);
    }
    doc.text(String(ln), x, yy);
    yy += lineHeight;
  }

  return yy;
}

/* ======================================================
   Header/Footer (A-Typical style)
   ====================================================== */

function addHeaderFooterAllPages(
  brandLogo: string,
  partnerLogo: string,
  title: string,
  tenantLogoUrl?: string
) {
  const totalPages = (doc as any).internal.getNumberOfPages();

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Header band
    setFillHex(LIGHT_GREY);
    doc.rect(MARGIN, HEADER_TOP, pageWidth - MARGIN * 2, HEADER_H, "F");

    const logoY = HEADER_TOP + 5;
    safeAddImage(brandLogo, MARGIN + 1, logoY, 18, 18);
    safeAddImage(partnerLogo, pageWidth - MARGIN - 19, logoY, 18, 18);

    if (tenantLogoUrl) {
      safeAddImage(tenantLogoUrl, pageWidth - MARGIN - 40, logoY, 18, 18);
    }

    // Title
doc.setFont(FONT_FAMILY as any, "bold");
    doc.setFontSize(H2_SIZE);
    setTextHex(BRAND_GREY);
    doc.text(String(title || "").trim(), MARGIN + 24, HEADER_TOP + 14);

    // Divider
    setDrawHex(BORDER_GREY);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, HEADER_TOP + HEADER_H, pageWidth - MARGIN, HEADER_TOP + HEADER_H);

    // Footer
doc.setFont(FONT_FAMILY as any, "normal");
    doc.setFontSize(FOOTER_SIZE);
    setTextHex(FOOTER_GREY);
    doc.text("© 2026 One Size Fits None™", MARGIN, pageHeight - 10);
    doc.text(`Page ${p} of ${totalPages}`, pageWidth - MARGIN, pageHeight - 10, {
      align: "right",
    } as any);
  }
}

/* ======================================================
   Text utilities
   ====================================================== */

function cleanLine(s: string) {
  return String(s || "")
    .replace(/\t/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isRuleLine(s: string) {
  const t = String(s || "").trim();
  return /^-{3,}$/.test(t) || /^_{3,}$/.test(t) || /^\*{3,}$/.test(t);
}

/* ======================================================
   Rendering primitives
   ====================================================== */

function renderH1(text: string) {
  ensureSpace(12);
  setTextHex(BRAND_GREY);
  yCursor = renderBlock({
    text,
    x: MARGIN,
    y: yCursor,
    maxWidth: pageWidth - MARGIN * 2,
    lineHeight: LINE_HEIGHT + 1.5,
    fontSize: H1_SIZE,
    fontStyle: "bold",
  });
  yCursor += SECTION_GAP;
}

function renderMetaLine(text: string) {
  setTextHex(TEXT_COLOR);
  yCursor = renderBlock({
    text,
    x: MARGIN,
    y: yCursor,
    maxWidth: pageWidth - MARGIN * 2,
    lineHeight: LINE_HEIGHT,
    fontSize: SMALL_SIZE,
    fontStyle: "normal",
  });
  yCursor += SECTION_GAP;
}

function renderSectionBanner(text: string) {
  const h = 10;
  ensureSpace(h + PARA_GAP);

  setFillHex(LIGHT_GREY);
  setDrawHex(BORDER_GREY);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, yCursor - 2, pageWidth - MARGIN * 2, h, 2, 2, "FD");

doc.setFont(FONT_FAMILY as any, "bold");
  doc.setFontSize(H2_SIZE);
  setTextHex(BRAND_ORANGE);
  doc.text(cleanLine(text), MARGIN + 3, yCursor + 5);

  yCursor += h + PARA_GAP;
}

function renderSubheading(text: string) {
  ensureSpace(8);
  setTextHex(BRAND_GREY);
  yCursor = renderBlock({
    text,
    x: MARGIN,
    y: yCursor,
    maxWidth: pageWidth - MARGIN * 2,
    lineHeight: LINE_HEIGHT,
    fontSize: H2_SIZE,
    fontStyle: "bold",
  });
  yCursor += PARA_GAP;
}

function renderParagraph(text: string) {
  setTextHex(TEXT_COLOR);
  yCursor = renderBlock({
    text,
    x: MARGIN,
    y: yCursor,
    maxWidth: pageWidth - MARGIN * 2,
    lineHeight: LINE_HEIGHT,
    fontSize: BODY_SIZE,
    fontStyle: "normal",
  });
  yCursor += PARA_GAP;
}

function renderIndentedBullet(text: string) {
  setTextHex(TEXT_COLOR);
  yCursor = renderBlock({
    text,
    x: MARGIN + 6,
    y: yCursor,
    maxWidth: pageWidth - MARGIN * 2 - 6,
    lineHeight: LINE_HEIGHT,
    fontSize: BODY_SIZE,
    fontStyle: "normal",
  });
  yCursor += PARA_GAP;
}

function renderNumbered(no: string, text: string) {
  const num = String(no || "").trim();
  const body = String(text || "").trim();
  const numX = MARGIN;
  const textX = MARGIN + 10;
  const maxWidth = pageWidth - MARGIN - textX;

  ensureSpace(LINE_HEIGHT * 2);

doc.setFont(FONT_FAMILY as any, "bold");
  doc.setFontSize(BODY_SIZE);
  setTextHex(TEXT_COLOR);
  doc.text(num, numX, yCursor);

doc.setFont(FONT_FAMILY as any, "normal");
  doc.setFontSize(BODY_SIZE);

  const lines = doc.splitTextToSize(body, maxWidth);
  let yy = yCursor;

  for (const ln of lines) {
    if (yy + LINE_HEIGHT > bottomLimit()) {
      addPage();
      yy = yCursor;
doc.setFont(FONT_FAMILY as any, "normal");
      doc.setFontSize(BODY_SIZE);
    }
    doc.text(String(ln), textX, yy);
    yy += LINE_HEIGHT;
  }

  yCursor = yy + PARA_GAP;
}

/* ======================================================
   Artifact detection
   ====================================================== */

function detectArtifactKind(
  title: string,
  content: string
): "questionPaper" | "answerKey" | "markingRubric" | "assessmentBlueprint" {
  const t = String(title || "").toLowerCase();
  if (t.includes("answer")) return "answerKey";
  if (t.includes("rubric")) return "markingRubric";
  if (t.includes("blueprint")) return "assessmentBlueprint";
  if (t.includes("question")) return "questionPaper";

  const c = String(content || "").toLowerCase();
  if (c.includes("answer key")) return "answerKey";
  if (c.includes("marking rubrics")) return "markingRubric";
  if (c.includes("assessment blueprint")) return "assessmentBlueprint";
  return "questionPaper";
}

/* ======================================================
   Parsing helpers
   - Fix orphan "2." line by binding to next non-empty line
   ====================================================== */

function normalizeLines(rawContent: string) {
  const lines = String(rawContent || "").replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let pendingNumber: string | null = null;

  for (const raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) continue;

    const onlyNum = trimmed.match(/^(\d+)\.$/);
    if (onlyNum) {
      pendingNumber = `${onlyNum[1]}.`;
      continue;
    }

    if (pendingNumber) {
      out.push(`${pendingNumber} ${trimmed}`);
      pendingNumber = null;
      continue;
    }

    out.push(trimmed);
  }

  if (pendingNumber) out.push(pendingNumber);

  return out;
}

/* ======================================================
   Artifact renderers
   ====================================================== */

function renderQuestionPaper(content: string) {
  const lines = normalizeLines(content);

  for (const ln of lines) {
    const t = cleanLine(ln);
    if (!t) continue;

    if (isRuleLine(t)) {
      yCursor += SECTION_GAP;
      continue;
    }

    const upper = t.toUpperCase();

    if (/^(SECTION\s+[A-Z]\b|GENERAL INSTRUCTIONS\b)/.test(upper)) {
      renderSectionBanner(t);
      continue;
    }

    if (/^(CLASS\b|SESSION\b|UNIT TEST\b|MID TERM\b|FINAL\b|TIME\b|DURATION\b|TOTAL MARKS\b)/i.test(t)) {
      renderSubheading(t);
      continue;
    }

    const nm = t.match(/^(\d+)\.\s+(.*)$/);
    if (nm) {
      renderNumbered(`${nm[1]}.`, nm[2]);
      continue;
    }

    if (/^[-*•]\s+/.test(t)) {
      renderIndentedBullet(`• ${t.replace(/^[-*•]\s+/, "")}`);
      continue;
    }

    renderParagraph(t);
  }
}

function renderAnswerKey(content: string) {
  const lines = normalizeLines(content);

  for (const ln of lines) {
    const t = cleanLine(ln);
    if (!t) continue;

    const upper = t.toUpperCase();

    if (upper === "ANSWER KEY") {
      renderSectionBanner("ANSWER KEY");
      continue;
    }

    const m1 = t.match(/^(\d+)\.\s+(.*)$/);
    if (m1) {
      renderNumbered(`${m1[1]}.`, m1[2]);
      continue;
    }

    const m2 = t.match(/^Q(\d+)\s*[:\-]\s*(.*)$/i);
    if (m2) {
      renderNumbered(`Q${m2[1]}.`, m2[2]);
      continue;
    }

    renderParagraph(t);
  }
}

type RubricLabel = "full_marks" | "partial_marks" | "common_errors";

function normalizeRubricLabel(s: string): RubricLabel | null {
  const t = String(s || "").trim().toLowerCase().replace(/\*\*/g, "");
  if (t.startsWith("full_marks") || t.startsWith("full marks")) return "full_marks";
  if (t.startsWith("partial_marks") || t.startsWith("partial marks")) return "partial_marks";
  if (t.startsWith("common_errors") || t.startsWith("common errors")) return "common_errors";
  return null;
}

function renderMarkingRubric(content: string) {
  const lines = normalizeLines(content);

  let currentQ: string | null = null;
  let currentLabel: RubricLabel | null = null;

  for (const ln of lines) {
    const t0 = cleanLine(ln);
    if (!t0) continue;

    const upper = t0.toUpperCase();

    if (upper === "MARKING RUBRICS" || upper === "MARKING RUBRIC" || upper === "RUBRICS") {
      renderSectionBanner("MARKING RUBRICS");
      continue;
    }

    const q1 = t0.match(/^Q(\d+)\b[:\-]?\s*$/i);
    const q2 = t0.match(/^Q(\d+)\.\s*$/i);
    const q3 = t0.match(/^Q(\d+)\s+/i);

    if (q1 || q2 || (q3 && t0.length <= 6)) {
      const qn = (q1 || q2 || q3)![1];
      currentQ = `Q${qn}`;
      currentLabel = null;
      yCursor += PARA_GAP;
      renderSectionBanner(currentQ);
      continue;
    }

    const lbl = normalizeRubricLabel(t0);
    if (lbl) {
      currentLabel = lbl;
      yCursor += 2;
      renderSubheading(
        lbl === "full_marks" ? "Full marks" : lbl === "partial_marks" ? "Partial marks" : "Common errors"
      );
      continue;
    }

    if (/^[-*•]\s+/.test(ln) || t0.startsWith("• ")) {
      const bullet = `• ${t0.replace(/^[-*•]\s+/, "").replace(/^•\s+/, "")}`;
      renderIndentedBullet(bullet);
      continue;
    }

    const inline = t0.match(/^(full_marks|partial_marks|common_errors)\s*:\s*(.+)$/i);
    if (inline) {
      const lab = normalizeRubricLabel(inline[1]);
      const rest = inline[2];
      if (lab) {
        currentLabel = lab;
        yCursor += 2;
        renderSubheading(
          lab === "full_marks" ? "Full marks" : lab === "partial_marks" ? "Partial marks" : "Common errors"
        );
        renderIndentedBullet(`• ${rest}`);
        continue;
      }
    }

    if (currentQ || currentLabel) {
      renderIndentedBullet(`• ${t0}`);
      continue;
    }

    renderParagraph(t0);
  }
}

function renderBlueprint(content: string) {
  const lines = normalizeLines(content);

  for (const ln of lines) {
    const t = cleanLine(ln);
    if (!t) continue;

    const upper = t.toUpperCase();

    if (upper === "ASSESSMENT BLUEPRINT") {
      renderSectionBanner("ASSESSMENT BLUEPRINT");
      continue;
    }

    if (isRuleLine(t)) {
      yCursor += SECTION_GAP;
      continue;
    }

    if (t.includes("|")) {
      doc.setFont(FONT_FAMILY as any, "normal");
      doc.setFontSize(SMALL_SIZE);
      setTextHex(TEXT_COLOR);

      const maxWidth = pageWidth - MARGIN * 2;
      const wrapped = doc.splitTextToSize(t, maxWidth);

      for (const w of wrapped) {
        ensureSpace(LINE_HEIGHT);
        doc.text(String(w), MARGIN, yCursor);
        yCursor += LINE_HEIGHT;
      }

      yCursor += PARA_GAP;
      continue;
    }

    if (/^[-*•]\s+/.test(t)) {
      renderIndentedBullet(`• ${t.replace(/^[-*•]\s+/, "")}`);
      continue;
    }

    const nt = t.match(/^(\d+)\.\s+(.*)$/);
    if (nt && nt[2].length < 90) {
      renderSectionBanner(`${nt[1]}. ${nt[2]}`);
      continue;
    }

    renderParagraph(t);
  }
}

/* ======================================================
   Public API (matches your UI call site)
   ====================================================== */

export function generateSingleArtifactPdf(
  title: string,
  content: string,
  inputs: AssessmentInputs,
  brandLogo: string,
  partnerLogo: string,
  tenantLogoUrl?: string
) {
  doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  pageHeight = doc.internal.pageSize.height;
  pageWidth = doc.internal.pageSize.width;

FONT_FAMILY = ensureRaleway(doc) ? "Raleway" : "helvetica";
doc.setFont(FONT_FAMILY as any, "normal");
  yCursor = BODY_START_Y;

  const normalizedContent = normalizeAssessorText(String(content || ""));
  const kind = detectArtifactKind(title, normalizedContent);

  renderH1(title);

  const meta = `Grade: ${inputs.grade}   Subject: ${inputs.subject}   Total Marks: ${inputs.totalMarks}   Duration: ${inputs.duration} min   Type: ${inputs.assessmentType}`;
  renderMetaLine(meta);

  if (kind === "questionPaper") renderQuestionPaper(normalizedContent);
  if (kind === "answerKey") renderAnswerKey(normalizedContent);
  if (kind === "markingRubric") renderMarkingRubric(normalizedContent);
  if (kind === "assessmentBlueprint") renderBlueprint(normalizedContent);

  addHeaderFooterAllPages(brandLogo, partnerLogo, title, tenantLogoUrl);

  const safeName = String(title || "Assessor")
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_");

  doc.save(`${safeName}.pdf`);
}
